import {useMemo, useState, useEffect, useContext, createContext, type FC} from 'react';
import {createTheme, ThemeProvider} from '@mui/material/styles';
import {type SupportedLanguages, type Theme} from 'interfaces';
import useLanguage from 'hooks/useLanguage';
import {useTranslation} from 'react-i18next';
import {themOptions} from 'styles/themes/themeHandler';
import {HotkeysProvider} from 'react-hotkeys-hook';
import {LocalizationProvider} from '@mui/x-date-pickers';
import {AdapterDateFns} from '@mui/x-date-pickers/AdapterDateFns';

export const SettingsContext = createContext<ISettings | undefined>(undefined);

const useSettingsContext = () => useContext(SettingsContext);

enum LocalStorageEnum {
  themeKey = 'theme',
  languageKey = 'language',
}

interface Settings {
  language: SupportedLanguages;
  theme: Theme;
}

const SettingsProvider: FC<any> = (props) => {
  const localStorageTheme = (localStorage.getItem(LocalStorageEnum.themeKey) as Theme) || 'light';
  const localStorageLanguage = (localStorage.getItem(LocalStorageEnum.languageKey) as SupportedLanguages) || 'en';
  const {i18n} = useTranslation();

  const [settings, setSettings] = useState<Settings>({
    language: localStorageLanguage,
    theme: localStorageTheme,
  });

  const setTheme = (theme: Theme): void => {
    localStorage.setItem(LocalStorageEnum.themeKey, theme);
    setSettings({...settings, theme});
  };

  const setLanguage = (language: SupportedLanguages): void => {
    localStorage.setItem(LocalStorageEnum.languageKey, language);
    setSettings({...settings, language});
    i18n.changeLanguage(language);
  };

  useEffect(() => {
    import.meta.env.NODE_ENV !== 'test' && i18n.changeLanguage(localStorageLanguage);
  }, []);

  const _settings = useMemo(
    () => ({
      theme: settings.theme,
      setTheme,
      language: settings.language,
      setLanguage,
    }),
    [settings],
  );

  const {datefnsLocale} = useLanguage();
  const muiTheme = createTheme(themOptions(settings.theme) as Record<string, unknown>);
  return (
    <SettingsContext.Provider value={_settings}>
      <HotkeysProvider initiallyActiveScopes={['SMOLT']}>
        <ThemeProvider theme={muiTheme}>
          <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={datefnsLocale}>
            {props.children}
          </LocalizationProvider>
        </ThemeProvider>
      </HotkeysProvider>
    </SettingsContext.Provider>
  );
};

export default SettingsProvider;

interface ISettings {
  theme: Theme;
  setTheme: (theme: Theme) => void;
  language: SupportedLanguages;
  setLanguage: (language: SupportedLanguages) => void;
}
